import React from "react";
import { motion } from "framer-motion";
import { FileText, Building, Mail, Phone, MapPin } from "lucide-react";

const Terms = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Terms of Service
          </h1>
          <div className="bg-white rounded-lg shadow-lg p-6 text-left max-w-2xl mx-auto">
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>Effective date:</strong> 02 May 2024
              </p>
              <p>
                <strong>Last updated:</strong> 02 May 2024
              </p>
              <p>
                <strong>Operating business name:</strong> Visapat
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Company Information */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-inter font-bold text-acBlack mb-6 flex items-center">
              <Building className="w-6 h-6 mr-3 text-acBlack" strokeWidth={1} />
              Company Information
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Legal entity (UK):</strong> Immigration Services Ltd
(trading as "Visapat")
              </p>
              <p>
                <strong>Company number:</strong> 1080174 (registered in England
                & Wales)
              </p>
              <p>
                <strong>Registered office:</strong> The Stable Yard, Vicarage
                Road, Stony Stratford, Milton Keynes, Buckinghamshire, MK11 1BN,
                United Kingdom
              </p>
              <p>
                <strong>Domain:</strong> Visapat.com
              </p>
              <p>
                <strong>Contact:</strong> legal@visapat.com
              </p>
            </div>
          </div>

          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              These Terms of Service ("Terms") govern use of our websites and
              the provision of immigration consulting services to individuals
              and families. By using the site or engaging our services you
              accept these Terms.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Definitions */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            1) Definitions
          </h2>
          <div className="space-y-4">
            {[
              {
                term: "Firm, we, us, our",
                definition:
                  'Immigration Services Ltd trading as "Visapat".',
              },
              {
                term: "You, client",
                definition:
                  "the person who visits the site or engages our services.",
              },
              {
                term: "Services",
                definition:
                  "immigration consulting, document preparation, coordination with third parties, and related support.",
              },
              {
                term: "Site",
                definition:
                  "Visapat.com, its subdomains, portals, forms, emails, and messaging channels operated by us.",
              },
              {
                term: "Local counsel",
                definition:
                  "a licensed attorney, regulated adviser, or representative engaged where required by law.",
              },
              {
                term: "Government fees",
                definition:
                  "fees charged by governments, consulates, visa centres, testing bodies, and similar agencies.",
              },
              {
                term: "Business day",
                definition:
                  "Monday to Friday, excluding public holidays in England and Wales.",
              },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <p className="text-gray-700">
                  <strong className="text-acBlack">{item.term}:</strong>{" "}
                  {item.definition}
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Who We Are */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            2) Who we are and what we do
          </h2>
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                <strong>2.1</strong> We are an immigration consulting practice
                focused on individuals and families. We are not a law firm and
                we do not provide legal representation. Where local law requires
                representation by a regulated adviser (e.g., OISC/SRA in the UK
                or licensed attorneys elsewhere), we coordinate with licensed
                local counsel who will be engaged under their own terms.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                <strong>2.2</strong> We are not affiliated with any government
                or immigration authority (e.g., UKVI/Home Office, USCIS, IRCC,
                embassies). We do not influence officers or expedite decisions
                outside published programmes.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Scope of Terms */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            3) Scope of these Terms
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            These Terms have three parts.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500">
              <h3 className="font-semibold text-acBlack mb-2">Part A</h3>
              <p className="text-gray-600 text-sm">
                Website Terms apply to all visitors.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-green-500">
              <h3 className="font-semibold text-acBlack mb-2">Part B</h3>
              <p className="text-gray-600 text-sm">
                Services Terms apply when you request paid services.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-purple-500">
              <h3 className="font-semibold text-acBlack mb-2">Part C</h3>
              <p className="text-gray-600 text-sm">
                General Legal Terms apply to both.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Part A - Website Terms */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 border-l-4 border-blue-500 pl-4">
            Part A - Website Terms
          </h2>

          <div className="space-y-12">
            {/* Use of the site */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                4) Use of the site
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>4.1</strong> Use the site lawfully, for its intended
                  purpose, and in accordance with these Terms. Do not attempt
                  security testing, scraping, reverse engineering, or any
                  activity that harms the site or users.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>4.2</strong> Where an account is offered, keep
                  credentials confidential. You are responsible for activity
                  under your account.
                </p>
              </div>
            </div>

            {/* Information on the site */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                5) Information on the site
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>5.1</strong> Site content is general information for
                  guidance only. It is not legal advice or a guarantee of
                  outcome. Rules change frequently and may vary by country and
                  personal circumstances.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>5.2</strong> We try to keep content accurate but do
                  not promise completeness, timeliness, or error-free material.
                </p>
              </div>
            </div>

            {/* User content */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                6) User content
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>6.1</strong> If you upload or submit content you
                  promise you have the right to share it and that it is accurate
                  and lawful.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>6.2</strong> You grant us a limited licence to store
                  and process that content to operate the site and deliver
                  requested Services. See our Privacy Policy for details.
                </p>
              </div>
            </div>

            {/* Intellectual property */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                7) Intellectual property
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>7.1</strong> The site, branding, text, graphics, and
                  templates are owned by us or our licensors. You may not copy,
                  sell, or reuse any part without permission.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>7.2</strong> You may download materials we provide for
                  your personal, non-commercial use related to your immigration
                  matter.
                </p>
              </div>
            </div>

            {/* Third-party links */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                8) Third-party links
              </h3>
              <p className="text-gray-700 leading-relaxed">
                The site may link to third parties. We do not control or endorse
                their content or practices. Use them at your discretion.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Part B - Services Terms */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 border-l-4 border-green-500 pl-4">
            Part B - Services Terms
          </h2>

          <div className="space-y-12">
            {/* Engagement process */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                9) Engagement process
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>9.1</strong> An engagement begins when you accept a
                  written proposal, letter of engagement, email quote, or online
                  checkout that references these Terms, and you pay any initial
                  invoice or retainer.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>9.2</strong> The specific scope, timelines,
                  deliverables, and fees for your matter will appear in the
                  engagement documents. If there is a conflict, the engagement
                  document prevails over these Terms.
                </p>
              </div>
            </div>

            {/* Our role */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                10) Our role
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>10.1</strong> We provide immigration consulting and
                  coordination services which may include: eligibility
                  assessments, strategy, document checklists, templates, form
                  completion, submissions where allowed, appointment scheduling,
                  interview preparation, status tracking, and post-decision
                  guidance.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>10.2</strong> If representation by a regulated
                  professional is required by law, we will introduce licensed
                  local counsel. You will contract with counsel directly and pay
                  their fees separately unless our engagement states otherwise.
                  We remain your day-to-day point of contact unless agreed
                  differently.
                </p>
              </div>
            </div>

            {/* Your responsibilities */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                11) Your responsibilities
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">a)</span>
                  <span>
                    Provide complete and accurate information and documents in a
                    timely manner.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">b)</span>
                  <span>
                    Tell us promptly about changes, travel plans, or deadlines.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">c)</span>
                  <span>
                    Review drafts, approve filings, and attend appointments.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">d)</span>
                  <span>
                    Pay our invoices and third-party fees that are your
                    responsibility.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">e)</span>
                  <span>
                    Comply with laws and instructions of government authorities.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">f)</span>
                  <span>
                    Keep copies of all documents you submit and receive.
                  </span>
                </li>
              </ul>
            </div>

            {/* No guarantees */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                12) No guarantees
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>12.1</strong> We do not guarantee approvals,
                  processing times, interview outcomes, or entry at a border.
                  Decisions rest with government authorities.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>12.2</strong> Timelines we share are estimates based
                  on current information and may change without notice.
                </p>
              </div>
            </div>

            {/* Fees, expenses, and taxes */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                13) Fees, expenses, and taxes
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>13.1</strong> Our fees are usually stage-based and
                  will be listed in your engagement. We may request a retainer
                  that we draw down against future work.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>13.2</strong> Government fees, visa centre charges,
                  medicals, police checks, translations, courier, travel,
                  evaluations, and counsel fees are not included unless stated.
                  You pay these directly where possible.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>13.3</strong> Prices are shown before taxes unless
                  stated. You are responsible for applicable taxes (e.g., VAT).
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>13.4</strong> Invoices are due upon receipt unless the
                  invoice states a due date. Late balances may incur interest at
                  the lower of 1.5% per month or the highest rate allowed by
                  law. We may pause work for overdue invoices.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>13.5</strong>{" "}
                  <strong>Currency & payment methods.</strong> Unless stated
                  otherwise, charges are in GBP and payable via the methods
                  listed on your invoice. Bank charges and FX costs are your
                  responsibility.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Part C - General Legal Terms */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 border-l-4 border-purple-500 pl-4">
            Part C - General Legal Terms
          </h2>

          <div className="space-y-12">
            {/* Privacy */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                24) Privacy
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Our processing of personal data is described in our Privacy
                Policy. By using the Site or Services you acknowledge that
                policy. If we appoint an EU representative or UK representative,
                their details will appear there.
              </p>
            </div>

            {/* Limitation of liability */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                26) Limitation of liability
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>26.1</strong> To the fullest extent permitted by law,
                  our total liability for all claims arising out of or related
                  to the Site or Services will not exceed the fees you paid to
                  us for the Services giving rise to the claim during the 12
                  months before the event.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>26.2</strong> We are not liable for indirect,
                  incidental, special, consequential, or punitive damages, loss
                  of profits, loss of business, or loss of data.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>26.3</strong> Nothing in these Terms limits liability
                  for death or personal injury caused by negligence, fraud, or
                  other liability that cannot be excluded by law.
                </p>
              </div>
            </div>

            {/* Governing law and disputes */}
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                29) Governing law and disputes
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>29.1</strong> If your engagement letter specifies
                  governing law and forum, that clause controls. If not, for all
                  clients contracting with our UK entity, the law of England and
                  Wales governs and the courts of England have exclusive
                  jurisdiction.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>29.2</strong> Consumers may have mandatory rights
                  under local law that override parts of this section.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>29.3</strong> Before filing a claim, each party will
                  attempt good faith resolution for at least 30 days after
                  written notice of the dispute.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Country-specific disclosures */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            Country-specific disclosures
          </h2>

          <div className="space-y-8">
            {/* United Kingdom */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                United Kingdom
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Services are provided by Immigration Services Ltd
                  (trading as "Visapat"), company number 1080174, registered in
                  England and Wales.
                </p>
                <p>
                  <strong>Complaints:</strong> complaints@visapat.com. We aim to
                  acknowledge within 3 business days and respond within 14
                  business days. If your matter involves regulated immigration
                  advice delivered through a regulated professional, your
                  engagement documents will include details of any external
                  complaints escalation routes applicable to that regulated
                  provider.
                </p>
              </div>
            </div>

            {/* European Union and EEA */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                European Union and EEA
              </h3>
              <p className="text-gray-700">
                If you are located in the EEA and we appoint an EU
                representative, contact details appear in the Privacy Policy.
                Cross-border transfers are protected by appropriate safeguards
                as described there.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Contact Information */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            Contact
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Questions about these Terms",
                email: "legal@visapat.com",
                icon: <FileText className="w-5 h-5" strokeWidth={1} />,
              },
              {
                title: "Billing questions & cancellations",
                email: "billing@visapat.com",
                icon: <Mail className="w-5 h-5" strokeWidth={1} />,
              },
              {
                title: "Privacy questions",
                email: "privacy@visapat.com",
                icon: <Mail className="w-5 h-5" strokeWidth={1} />,
              },
              {
                title: "Complaints",
                email: "complaints@visapat.com",
                icon: <Mail className="w-5 h-5" strokeWidth={1} />,
              },
            ].map((contact, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm border"
              >
                <div className="flex items-center mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3 text-acBlack">
                    {contact.icon}
                  </div>
                  <h3 className="font-semibold text-acBlack">
                    {contact.title}
                  </h3>
                </div>
                <a
                  href={`mailto:${contact.email}`}
                  className="text-acBlack hover:text-gray-700 transition-colors"
                >
                  {contact.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Service Level Summary */}
      <motion.section className="py-16 px-6 bg-blue-50 " {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">
            Optional annex: service level summary (non-contractual)
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-acBlack mb-2">Response time</h4>
              <p className="text-gray-600 text-sm">
                24 to 72 hours on business days
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-acBlack mb-2">
                Priority handling
              </h4>
              <p className="text-gray-600 text-sm">
                Time-sensitive steps prioritised
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-acBlack mb-2">
                Availability window
              </h4>
              <p className="text-gray-600 text-sm">
                London 09:00 to 18:00 (UK local time)
              </p>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Terms;
